import * as React from "react";
import classnames from "classnames";
import { Button, Elem, ElemAfter } from "@alm/flow-ds/components";
import { CommonFormElementProps } from "@alm/flow-ds/dist/components/_common/Elem";
import { DatePicker } from "@alm/flow-ds/components";
import { DateOperators } from "../../../lib/api/services/workflow";
import { useTranslation } from "react-i18next";
import { isRTL } from "utils/getLanguageDir";
import { useCallback, useEffect, useMemo, useState } from "react";
import { handleDate, validateISO } from "../../../lib/dates";

export interface RadioProps extends CommonFormElementProps {
	options: Array<{ value: string; label: string }>;
	onChange: (value: string, operatorValue: DateOperators) => void;
	value: string;
	operator: DateOperators;
	close: () => void;
	setActive: React.Dispatch<React.SetStateAction<number | null>>;
}

const ActivityInstancesDates = ({
	className,
	dirty,
	disabled,
	feedback,
	hint,
	id,
	label,
	onChange,
	options,
	required,
	status,
	value,
	operator,
	close,
	setActive,
}: RadioProps) => {
	const { t, i18n } = useTranslation();

	const [date, setDate] = useState<string>(
		value.includes(",") ? value.substring(0, value.indexOf(",")) : value
	);
	const [date2, setDate2] = useState<string>(
		value.includes(",") ? value.substring(value.indexOf(",") + 1) : ""
	);
	const [error, setError] = useState<string | null>(null);

	const [tempOperator, setTempOperator] = useState<DateOperators>(operator);

	const [width, setWidth] = useState(window.innerWidth);

	const handleWindowSizeChange = () => setWidth(window.innerWidth);

	useEffect(() => {
		window.addEventListener("resize", handleWindowSizeChange);
		return () => {
			window.removeEventListener("resize", handleWindowSizeChange);
		};
	}, []);

	const handleOnChange = (value: string, calendarEvent: boolean) => {
		setDate(value);
	};

	const handleOnChange2 = (value: string, calendarEvent: boolean) => {
		setDate2(value);
	};

	const handleUpdate = useCallback(() => {
		let finalDate =
			tempOperator === "EXACT"
				? date
				: handleDate(date, i18n.language, t);
		const finalDate2 = handleDate(date2, i18n.language, t);
		if (tempOperator === "ALL") {
			onChange(finalDate, tempOperator);
			close();
		} else if (
			validateISO(finalDate) &&
			((tempOperator === "BETWEEN" && validateISO(finalDate2)) ||
				tempOperator !== "BETWEEN")
		) {
			if (tempOperator === "BETWEEN") {
				if (finalDate < finalDate2) {
					finalDate = finalDate + "," + finalDate2;
					onChange(finalDate, tempOperator);
					close();
				} else {
					setError("range");
				}
			} else {
				onChange(finalDate, tempOperator);
				close();
			}
		} else {
			setError("validation");
		}
	}, [close, date, date2, i18n.language, onChange, t, tempOperator]);

	const datePickersProps = useMemo(() => {
		return {
			locale: i18n.language,
			monthLabel: t("elements.date.month"),
			yearLabel: t("elements.date.year"),
			nextMonthLabel: t("elements.date.nextMonth"),
			prevMonthLabel: t("elements.date.prevMonth"),
			todayLabel: t("elements.date.today"),
			closeLabel: t("elements.date.close"),
			labelHidden: true,
			direction: (isRTL() ? "rtl" : "ltr") as "rtl" | "ltr",
			status: error !== null ? "error" : "",
		};
	}, [error, i18n.language, t]);

	const datePickers = (operator: DateOperators) => {
		switch (operator) {
			case "ALL":
				return;

			case "AFTER":
			case "BEFORE":
			case "EXACT":
				return (
					<DatePicker
						{...datePickersProps}
						onChange={handleOnChange}
						label="ActivityInstancesDatePicker"
						value={date}
						feedback={
							error !== null
								? t(`main.activityInstances.text.${error}`)
								: undefined
						}
					/>
				);

			case "BETWEEN":
				return (
					<div className={`d-flex ${width < 576 ? "" : "flex-row"}`}>
						<DatePicker
							{...datePickersProps}
							onChange={handleOnChange}
							label="ActivityInstancesDatePickerBefore"
							value={date}
							feedback={
								error !== null && width > 575
									? t(`main.activityInstances.text.${error}`)
									: undefined
							}
						/>
						<span className="dropdown-item-text">and</span>
						<DatePicker
							{...datePickersProps}
							onChange={handleOnChange2}
							label="ActivityInstancesDatePickerAfter"
							value={date2}
							feedback={
								error !== null && width < 576
									? t(`main.activityInstances.text.${error}`)
									: undefined
							}
						/>
					</div>
				);

			default:
				return;
		}
	};

	useEffect(() => {
		if (error) setError(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [date, date2]);

	return (
		<>
			<Elem
				aria-label={label}
				className={classnames("elem-radio", className)}
				dirty={dirty}
				status={status}>
				<fieldset>
					{options ? (
						<div className="elem-radio__options">
							{options.map((o, i) => (
								<div
									className="form-check elem-radio__element"
									key={`${id}_${i}`}>
									<input
										type="radio"
										name={id}
										id={`${id}_${i}`}
										value={o.value}
										checked={o.value === tempOperator}
										required={required}
										disabled={disabled}
										onChange={event => {
											setError(null);
											setTempOperator(
												event.target
													.value as DateOperators
											);
										}}
										className="form-check-input elem-radio__option"
									/>
									<label
										className="form-check-label elem__label"
										htmlFor={`${id}_${i}`}>
										{o.label || o.value}
									</label>
									{o.value === tempOperator &&
										datePickers(tempOperator)}
								</div>
							))}
						</div>
					) : null}
					<ElemAfter hint={hint} feedback={feedback} />
				</fieldset>
			</Elem>
			<div className="dropdown-divider"></div>
			<div className="activity-instances-dateDropdown--footer">
				<Button
					className="btn ui-btn btn-outline-primary activity-instances-dateDropdown--update"
					name="Update"
					label="Update"
					onClick={handleUpdate}
				/>
				<Button
					className="btn ui-btn btn-basic--primary activity-instances-dateDropdown--cancel"
					name="Cancel"
					label="Cancel"
					onClick={close}
					onFocus={() => setActive(0)}
					onBlur={() => setActive(null)}
				/>
			</div>
		</>
	);
};

export default ActivityInstancesDates;
